/**
 * DOM HUD overlay (HANDOFF §4.1 / §6 Phase 2 Agent D).
 *
 * The field is Canvas; the HUD chrome is a minimal DOM overlay built on top:
 * both scores (under-par green / over-par red per §8), a per-side energy bar, the
 * match clock (mm:ss), and a hint line. Reads sim state ONLY — never mutates it,
 * never emits input. Agent E owns the card hand / overlays on disjoint files; this
 * module owns ONLY the scoreboard/energy/clock/hint surface.
 *
 * §8 colours come from src/styles/tokens.css custom properties via `var(--…)`, so
 * tokens.css stays the single source of truth (no hex duplicated here).
 *
 * The DOM is built once in the factory; `update(state)` only writes textContent /
 * width / className on cached nodes — no per-call element creation (§7).
 */
import type { GameState, SideState } from '../sim/types.ts';

export interface Hud {
  /** Update the DOM scoreboard / energy / clock / hint from sim state (read-only). */
  update(state: GameState): void;
}

/** Format a golf score relative to par: 0 → "E", +n → "+n", −n → "−n". */
function formatScore(n: number): string {
  if (n === 0) return 'E';
  return n > 0 ? `+${n}` : `−${Math.abs(n)}`; // proper minus sign (§8 typography)
}

/** Format seconds → "m:ss" (clamped at 0). */
function formatClock(seconds: number): string {
  const t = Math.max(0, Math.floor(seconds));
  const m = Math.floor(t / 60);
  const s = t % 60;
  return `${m}:${s < 10 ? '0' : ''}${s}`;
}

/** Score-class per §8: under par → good (green), over par → bad (red), level → neutral. */
function scoreClass(n: number): string {
  if (n < 0) return 'fc-good';
  if (n > 0) return 'fc-bad';
  return 'fc-even';
}

/**
 * Hint copy derived purely from swing/match phase. Read-only; mirrors the
 * prototype's aim-hint flow so the player always knows the next tap.
 */
function hintFor(state: GameState): string {
  if (state.phase === 'final') {
    if (state.winner === 'you') return 'You win — lowest card takes it.';
    if (state.winner === 'rival') return 'The rival posted the lower card.';
    return 'Dead heat.';
  }
  if (state.phase === 'playoff') return 'Playoff — closest to the pin wins.';
  switch (state.swing.phase) {
    case 'aim':
      return 'Drag to aim, then release to start the swing.';
    case 'backswing':
      return 'Tap at the top of the backswing (POWER).';
    case 'impact':
      return 'Tap in the tight gold zone (IMPACT).';
    default:
      return 'Pick a club, drag to aim, then time the two-tap swing. Read the break.';
  }
}

/** One-time CSS injection for the HUD overlay (scoped under .fc-hud). */
const HUD_STYLE_ID = 'fc-hud-style';
const HUD_CSS = `
.fc-hud{position:absolute;inset:0;pointer-events:none;font-family:var(--font-body,system-ui,sans-serif);color:var(--cream,#f4efe2);}
.fc-hud-board{position:absolute;top:0;left:0;right:0;display:flex;align-items:flex-start;
  justify-content:space-between;gap:8px;padding:calc(8px + env(safe-area-inset-top)) 14px 8px;
  background:linear-gradient(180deg,var(--dusk-2,#1a3145),transparent);}
.fc-hud-side{display:flex;flex-direction:column;align-items:center;gap:3px;min-width:104px;}
.fc-hud-side .fc-who{font-family:var(--font-pixel,monospace);font-size:8px;letter-spacing:.04em;text-transform:uppercase;opacity:.7;}
.fc-hud-side .fc-val{font-family:var(--font-pixel,monospace);font-weight:400;font-size:16px;line-height:1;}
.fc-hud-side .fc-meta{font-size:9px;opacity:.55;letter-spacing:.05em;}
.fc-hud-side .fc-sclock{font-family:var(--font-mono,ui-monospace,monospace);font-size:10px;opacity:.7;
  letter-spacing:.04em;margin-top:1px;min-height:12px;}
.fc-hud-side .fc-sclock.is-urgent{color:var(--flag,#e0463a);opacity:1;font-weight:700;}
.fc-val.fc-good{color:var(--turf-lit,#34a96b);}
.fc-val.fc-bad{color:var(--flag,#e0463a);}
.fc-val.fc-even{color:var(--cream,#f4efe2);}
.fc-hud-timer{font-family:var(--font-pixel,monospace);font-size:15px;font-weight:400;letter-spacing:0;
  color:var(--gold,#f2c14e);text-align:center;line-height:1;transform-origin:center;}
.fc-hud-timer.is-final{color:var(--flag,#e0463a);}
.fc-hud-timer .fc-lbl{display:block;font-family:var(--font-pixel,monospace);font-size:5px;white-space:nowrap;
  letter-spacing:.1em;opacity:.6;text-transform:uppercase;margin-top:5px;color:var(--cream,#f4efe2);}
.fc-hud-energy{position:absolute;left:14px;right:14px;bottom:calc(8px + env(safe-area-inset-bottom));
  display:flex;flex-direction:column;gap:4px;}
.fc-hud-ebar{height:10px;border-radius:6px;background:#1d2b38;overflow:hidden;position:relative;border:1px solid #2c4255;}
.fc-hud-efill{height:100%;width:0%;border-radius:6px;background:linear-gradient(90deg,var(--gold,#f2c14e),#f0863a);transition:width .12s linear;}
.fc-hud-efill.fc-rival{background:linear-gradient(90deg,var(--flag,#e0463a),#b22f26);}
.fc-help-btn{position:absolute;left:calc(8px + env(safe-area-inset-left));bottom:calc(34px + env(safe-area-inset-bottom));
  width:30px;height:30px;border-radius:50%;border:1px solid #2c4255;background:rgba(8,16,22,.6);color:var(--cream,#f4efe2);
  font-family:var(--font-display,system-ui),sans-serif;font-weight:800;font-size:16px;line-height:1;
  display:flex;align-items:center;justify-content:center;pointer-events:auto;cursor:pointer;
  -webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);}
.fc-help-btn.is-open{border-color:var(--gold,#f2c14e);color:var(--gold,#f2c14e);}
.fc-help-panel{position:absolute;left:50%;bottom:calc(72px + env(safe-area-inset-bottom));transform:translateX(-50%);
  display:none;font-size:12px;line-height:1.45;letter-spacing:.02em;background:rgba(8,16,22,.94);color:var(--cream,#f4efe2);
  padding:12px 16px;border-radius:14px;border:1px solid #2c4255;max-width:78%;text-align:center;
  box-shadow:0 8px 24px rgba(0,0,0,.5);}
.fc-help-panel.is-open{display:block;}
`;

function ensureStyle(): void {
  if (typeof document === 'undefined') return;
  if (document.getElementById(HUD_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = HUD_STYLE_ID;
  style.textContent = HUD_CSS;
  document.head.appendChild(style);
}

interface SideNodes {
  readonly val: HTMLElement;
  readonly meta: HTMLElement;
  readonly efill: HTMLElement;
  readonly sclock: HTMLElement;
}

/** Shot-clock seconds at/below which the readout turns urgent (red). */
const SHOT_CLOCK_URGENT = 7;

function buildSide(who: string, rival: boolean): { root: HTMLElement; nodes: SideNodes } {
  const root = document.createElement('div');
  root.className = 'fc-hud-side';
  const whoEl = document.createElement('span');
  whoEl.className = 'fc-who';
  whoEl.textContent = who;
  const val = document.createElement('span');
  val.className = 'fc-val fc-even';
  val.textContent = 'E';
  const meta = document.createElement('span');
  meta.className = 'fc-meta';
  meta.textContent = '0 shots';
  // Shot clock (anti-turtle) — shown for the human side only; the AI never lets it run down.
  const sclock = document.createElement('span');
  sclock.className = 'fc-sclock';
  root.append(whoEl, val, meta, sclock);
  // The energy fill node lives in the energy panel; created here for grouping.
  const efill = document.createElement('div');
  efill.className = rival ? 'fc-hud-efill fc-rival' : 'fc-hud-efill';
  return { root, nodes: { val, meta, efill, sclock } };
}

/**
 * Create the HUD overlay inside `root` (HANDOFF §4.1).
 *
 * Builds the DOM once and returns an `update(state)` that reflects sim state on
 * each frame. Pure read of state; emits no actions.
 */
export function createHud(root: HTMLElement): Hud {
  ensureStyle();

  // Headless guard (the node mock-context check has no DOM): no-op update.
  if (typeof document === 'undefined') {
    return { update() {} };
  }

  const hud = document.createElement('div');
  hud.className = 'fc-hud';

  // --- scoreboard ---
  const board = document.createElement('div');
  board.className = 'fc-hud-board';

  const you = buildSide('You', false);
  const rival = buildSide('Rival', true);

  const timer = document.createElement('div');
  timer.className = 'fc-hud-timer';
  const timerVal = document.createElement('span');
  timerVal.textContent = '3:00';
  const timerLbl = document.createElement('span');
  timerLbl.className = 'fc-lbl';
  timerLbl.textContent = 'Match Play';
  timer.append(timerVal, timerLbl);

  board.append(you.root, timer, rival.root);

  // --- energy bars (two stacked: you on top, rival below) ---
  const energy = document.createElement('div');
  energy.className = 'fc-hud-energy';
  const youBar = document.createElement('div');
  youBar.className = 'fc-hud-ebar';
  youBar.appendChild(you.nodes.efill);
  const rivalBar = document.createElement('div');
  rivalBar.className = 'fc-hud-ebar';
  rivalBar.appendChild(rival.nodes.efill);
  energy.append(youBar, rivalBar);

  // --- help: a "?" button on the side that pops up the current instructions ---
  const helpBtn = document.createElement('button');
  helpBtn.type = 'button';
  helpBtn.className = 'fc-help-btn';
  helpBtn.textContent = '?';
  helpBtn.setAttribute('aria-label', 'How to play');
  const helpPanel = document.createElement('div');
  helpPanel.className = 'fc-help-panel';
  helpPanel.textContent = hintFor({ phase: 'playing', swing: { phase: 'idle' } } as GameState);
  let helpOpen = false;
  const setHelp = (open: boolean): void => {
    helpOpen = open;
    helpBtn.classList.toggle('is-open', open);
    helpPanel.classList.toggle('is-open', open);
  };
  helpBtn.addEventListener('click', () => setHelp(!helpOpen));

  hud.append(board, energy, helpPanel, helpBtn);
  root.appendChild(hud);

  // Cached state to skip redundant DOM writes (cheap diffing — §7).
  let lastClock = '';
  let lastHint = '';

  function updateSide(side: SideState, nodes: SideNodes, maxEnergy: number, showClock: boolean): void {
    const text = formatScore(side.score);
    if (nodes.val.textContent !== text) nodes.val.textContent = text;
    const cls = `fc-val ${scoreClass(side.score)}`;
    if (nodes.val.className !== cls) nodes.val.className = cls;
    const metaText = `${side.shots} ${side.shots === 1 ? 'shot' : 'shots'}`;
    if (nodes.meta.textContent !== metaText) nodes.meta.textContent = metaText;
    const pct = `${Math.max(0, Math.min(1, side.energy / maxEnergy)) * 100}%`;
    if (nodes.efill.style.width !== pct) nodes.efill.style.width = pct;
    // Shot clock (anti-turtle): a "shoot or bleed strokes" countdown for the human side.
    const clockText = showClock ? `⏱ ${Math.max(0, Math.ceil(side.shotClock))}s` : '';
    if (nodes.sclock.textContent !== clockText) nodes.sclock.textContent = clockText;
    const urgent = showClock && side.shotClock <= SHOT_CLOCK_URGENT;
    nodes.sclock.classList.toggle('is-urgent', urgent);
  }

  function update(state: GameState): void {
    const playing = state.phase === 'playing';
    updateSide(state.you, you.nodes, state.maxEnergy, playing);
    updateSide(state.rival, rival.nodes, state.maxEnergy, false);

    // Final stretch: turn the clock red and POP it once per second — that per-second cadence is the
    // music's beat (60 BPM = 1 beat/second), so the timer visibly ticks in time with the track.
    const finalCountdown = state.phase === 'playing' && state.clock <= 10;
    if (timer.classList.contains('is-final') !== finalCountdown) timer.classList.toggle('is-final', finalCountdown);

    const clock = formatClock(state.clock);
    if (clock !== lastClock) {
      timerVal.textContent = clock;
      lastClock = clock;
      if (finalCountdown && typeof timer.animate === 'function') {
        timer.animate([{ transform: 'scale(1.16)' }, { transform: 'scale(1)' }], { duration: 620, easing: 'ease-out' });
      }
    }

    // Keep the help text current (contextual), but only touch the DOM when the
    // panel is open and the text actually changed.
    if (helpOpen) {
      const h = hintFor(state);
      if (h !== lastHint) {
        helpPanel.textContent = h;
        lastHint = h;
      }
    }
  }

  // Paint an initial frame is the caller's job (it owns the state); leave defaults.
  return { update };
}
